/**
 * Constantes pour la gestion des statuts des bénéficiaires
 * Système complet avec 5 statuts et gestion avancée
 */

// ========== STATUTS DISPONIBLES ==========

export const BENEFICIARY_STATUS = {
  ACTIVE: 'actif',
  PENDING: 'en_attente',
  SUSPENDED: 'suspendu',
  INACTIVE: 'inactif',
  ARCHIVED: 'archive'
};

// ========== LABELS DES STATUTS ==========

export const STATUS_LABELS = {
  [BENEFICIARY_STATUS.ACTIVE]: 'Actif',
  [BENEFICIARY_STATUS.PENDING]: 'En attente',
  [BENEFICIARY_STATUS.SUSPENDED]: 'Suspendu',
  [BENEFICIARY_STATUS.INACTIVE]: 'Inactif',
  [BENEFICIARY_STATUS.ARCHIVED]: 'Archivé'
};

// ========== DESCRIPTIONS DES STATUTS ==========

export const STATUS_DESCRIPTIONS = {
  [BENEFICIARY_STATUS.ACTIVE]: 'Bénéficiaire actif en service normal',
  [BENEFICIARY_STATUS.PENDING]: 'Nouveau bénéficiaire en cours d\'intégration (dossier incomplet)',
  [BENEFICIARY_STATUS.SUSPENDED]: 'Suspension temporaire avec date de fin et réactivation automatique',
  [BENEFICIARY_STATUS.INACTIVE]: 'En pause - À relancer pour connaître l\'évolution de la situation',
  [BENEFICIARY_STATUS.ARCHIVED]: 'Fin définitive du service'
};

// ========== COULEURS DES BADGES ==========

export const STATUS_COLORS = {
  [BENEFICIARY_STATUS.ACTIVE]: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-800 dark:text-green-200',
    border: 'border-green-300 dark:border-green-700',
    solid: 'bg-green-600'
  },
  [BENEFICIARY_STATUS.PENDING]: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-800 dark:text-yellow-200',
    border: 'border-yellow-300 dark:border-yellow-700',
    solid: 'bg-yellow-600'
  },
  [BENEFICIARY_STATUS.SUSPENDED]: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-200',
    border: 'border-orange-300 dark:border-orange-700',
    solid: 'bg-orange-600'
  },
  [BENEFICIARY_STATUS.INACTIVE]: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-800 dark:text-gray-200',
    border: 'border-gray-300 dark:border-gray-600',
    solid: 'bg-gray-600'
  },
  [BENEFICIARY_STATUS.ARCHIVED]: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-400 dark:border-gray-600',
    solid: 'bg-gray-700'
  }
};

// ========== RAISONS DE SUSPENSION ==========

export const SUSPENSION_REASONS = [
  { value: 'hospitalisation', label: 'Hospitalisation' },
  { value: 'vacances_prolongees', label: 'Vacances prolongées' },
  { value: 'probleme_financement', label: 'Problème de financement temporaire' },
  { value: 'attente_renouvellement', label: 'Attente de renouvellement de contrat' },
  { value: 'autre', label: 'Autre (à préciser)' }
];

// ========== RAISONS D'ARCHIVAGE ==========

export const ARCHIVE_REASONS = [
  { value: 'deces', label: 'Décès' },
  { value: 'demenagement', label: 'Déménagement hors zone' },
  { value: 'fin_service', label: 'Fin de service (amélioration de l\'état)' },
  { value: 'changement_prestataire', label: 'Changement de prestataire' },
  { value: 'non_paiement', label: 'Non-paiement persistant' },
  { value: 'autre', label: 'Autre (à préciser)' }
];

// ========== TRANSITIONS AUTORISÉES ==========

/**
 * Définit les transitions de statut autorisées
 * Format: { statutActuel: [statutsPossibles] }
 */
export const ALLOWED_TRANSITIONS = {
  [BENEFICIARY_STATUS.PENDING]: [
    BENEFICIARY_STATUS.ACTIVE,
    BENEFICIARY_STATUS.INACTIVE
    // Note: Utiliser le modal d'archivage avancé pour archiver
  ],
  [BENEFICIARY_STATUS.ACTIVE]: [
    BENEFICIARY_STATUS.PENDING,
    BENEFICIARY_STATUS.SUSPENDED,
    BENEFICIARY_STATUS.INACTIVE
    // Note: Utiliser le modal d'archivage avancé pour archiver
  ],
  [BENEFICIARY_STATUS.SUSPENDED]: [
    BENEFICIARY_STATUS.ACTIVE,
    BENEFICIARY_STATUS.INACTIVE
    // Note: Utiliser le modal d'archivage avancé pour archiver
  ],
  [BENEFICIARY_STATUS.INACTIVE]: [
    BENEFICIARY_STATUS.ACTIVE,
    BENEFICIARY_STATUS.SUSPENDED
    // Note: Utiliser le modal d'archivage avancé pour archiver
  ],
  [BENEFICIARY_STATUS.ARCHIVED]: [
    // TEMPORAIRE: Permettre de ré-archiver les anciens bénéficiaires archivés
    // avec l'ancien système (isArchived: true) pour qu'ils passent au nouveau système
    // Cette option permet de mettre à jour les raisons, dates et historique
    BENEFICIARY_STATUS.ARCHIVED
  ]
};

// ========== ICÔNES DES STATUTS ==========

export const STATUS_ICONS = {
  [BENEFICIARY_STATUS.ACTIVE]: '✓',
  [BENEFICIARY_STATUS.PENDING]: '⏳',
  [BENEFICIARY_STATUS.SUSPENDED]: '⏸',
  [BENEFICIARY_STATUS.INACTIVE]: '●',
  [BENEFICIARY_STATUS.ARCHIVED]: '📦'
};

// ========== RÈGLES MÉTIER ==========

/**
 * Règles spécifiques pour chaque statut
 */
export const STATUS_RULES = {
  [BENEFICIARY_STATUS.ACTIVE]: {
    allowBilling: true,
    allowAppointments: true,
    requiresAttention: false,
    canReceiveNotifications: true
  },
  [BENEFICIARY_STATUS.PENDING]: {
    allowBilling: true, // Facturation normale
    allowAppointments: true,
    requiresAttention: true, // Nécessite attention pour compléter dossier
    canReceiveNotifications: true
  },
  [BENEFICIARY_STATUS.SUSPENDED]: {
    allowBilling: true, // Facturation normale
    allowAppointments: false, // Pas de nouveaux RDV
    requiresAttention: true, // Vérifier date de fin
    canReceiveNotifications: true,
    requiresEndDate: true, // Date de fin obligatoire
    autoReactivation: true // Réactivation automatique possible
  },
  [BENEFICIARY_STATUS.INACTIVE]: {
    allowBilling: true, // Facturation normale
    allowAppointments: true,
    requiresAttention: true, // À relancer
    canReceiveNotifications: true,
    needsFollowUp: true // Besoin de suivi/relance
  },
  [BENEFICIARY_STATUS.ARCHIVED]: {
    allowBilling: false, // Pas de facturation pour archivés
    allowAppointments: false,
    requiresAttention: false,
    canReceiveNotifications: false,
    isPermanent: true // Changement définitif
  }
};

// ========== MESSAGES DE TRANSITION ==========

/**
 * Messages à afficher lors des transitions de statut
 */
export const TRANSITION_MESSAGES = {
  to_active: {
    success: 'Bénéficiaire activé avec succès',
    confirm: 'Activer ce bénéficiaire ?'
  },
  to_pending: {
    success: 'Bénéficiaire mis en attente',
    confirm: 'Mettre ce bénéficiaire en attente ?',
    warning: 'Le dossier devra être complété avant activation'
  },
  to_suspended: {
    success: 'Bénéficiaire suspendu avec succès',
    confirm: 'Suspendre ce bénéficiaire ?',
    warning: 'Les rendez-vous futurs seront annulés pendant la période de suspension'
  },
  to_inactive: {
    success: 'Bénéficiaire marqué comme inactif',
    confirm: 'Marquer ce bénéficiaire comme inactif ?',
    warning: 'Pensez à le relancer pour connaître l\'évolution de sa situation'
  },
  to_archived: {
    success: 'Bénéficiaire archivé avec succès',
    confirm: 'Archiver ce bénéficiaire ? Cette action est définitive.',
    warning: 'Tous les rendez-vous futurs seront supprimés et le bénéficiaire ne pourra plus être réactivé'
  }
};

// ========== FILTRES RAPIDES ==========

/**
 * Configuration des filtres rapides pour l'interface
 */
export const QUICK_FILTERS = [
  {
    id: 'all',
    label: 'Tous',
    statuses: Object.values(BENEFICIARY_STATUS)
  },
  {
    id: 'active_only',
    label: 'Actifs uniquement',
    statuses: [BENEFICIARY_STATUS.ACTIVE]
  },
  {
    id: 'needs_attention',
    label: 'Nécessitent attention',
    statuses: [BENEFICIARY_STATUS.PENDING, BENEFICIARY_STATUS.SUSPENDED, BENEFICIARY_STATUS.INACTIVE]
  },
  {
    id: 'suspended_ending_soon',
    label: 'Suspensions se terminant bientôt',
    statuses: [BENEFICIARY_STATUS.SUSPENDED],
    filter: (beneficiary) => {
      if (!beneficiary.suspensionDetails?.endDate) return false;
      const endDate = beneficiary.suspensionDetails.endDate.toDate();
      const daysUntilEnd = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilEnd <= 7 && daysUntilEnd > 0;
    }
  },
  {
    id: 'inactive_long_term',
    label: 'Inactifs > 30 jours',
    statuses: [BENEFICIARY_STATUS.INACTIVE],
    filter: (beneficiary) => {
      const lastChange = beneficiary.statusHistory?.slice(-1)[0];
      if (!lastChange || lastChange.newStatus !== BENEFICIARY_STATUS.INACTIVE) return false;
      const daysSince = Math.ceil((new Date() - lastChange.changedAt.toDate()) / (1000 * 60 * 60 * 24));
      return daysSince > 30;
    }
  }
];

// ========== HELPERS ==========

/**
 * Vérifie si une transition de statut est autorisée
 * @param {string} currentStatus - Statut actuel
 * @param {string} newStatus - Nouveau statut souhaité
 * @returns {boolean}
 */
export const isTransitionAllowed = (currentStatus, newStatus) => {
  if (!currentStatus || !newStatus) return false;
  if (currentStatus === newStatus) return false; // Pas de changement

  const allowedStatuses = ALLOWED_TRANSITIONS[currentStatus];
  return allowedStatuses && allowedStatuses.includes(newStatus);
};

/**
 * Récupère les informations de couleur pour un statut
 * @param {string} status - Le statut
 * @returns {object} Objet avec les classes de couleur
 */
export const getStatusColor = (status) => {
  return STATUS_COLORS[status] || STATUS_COLORS[BENEFICIARY_STATUS.ACTIVE];
};

/**
 * Récupère le label d'un statut
 * @param {string} status - Le statut
 * @returns {string} Label en français
 */
export const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status;
};

/**
 * Récupère les règles métier pour un statut
 * @param {string} status - Le statut
 * @returns {object} Règles métier
 */
export const getStatusRules = (status) => {
  return STATUS_RULES[status] || STATUS_RULES[BENEFICIARY_STATUS.ACTIVE];
};

/**
 * Vérifie si un bénéficiaire nécessite une attention particulière
 * @param {object} beneficiary - Le bénéficiaire
 * @returns {boolean}
 */
export const requiresAttention = (beneficiary) => {
  const rules = getStatusRules(beneficiary.status);
  return rules.requiresAttention || false;
};

/**
 * Récupère la raison d'un statut à partir de son code
 * @param {string} type - 'suspension' ou 'archive'
 * @param {string} reasonCode - Code de la raison
 * @returns {string} Label de la raison
 */
export const getReasonLabel = (type, reasonCode) => {
  const reasons = type === 'suspension' ? SUSPENSION_REASONS : ARCHIVE_REASONS;
  const reason = reasons.find(r => r.value === reasonCode);
  return reason ? reason.label : reasonCode;
};
